import React, { useEffect, useMemo, useState } from "react";
import { APIUS } from "../../../../../../../../config/apius";
import { APIMS } from "../../../../../../../../config/apims";
import { APISD } from "../../../../../../../../config/apisd";

const gradeNumber = (level) => Number(String(level?.name_level || "").match(/\d+/)?.[0] || 0);

export default function TambahSiswaModal({ 
  show, onHide, onSave, context = {}, apiContext = {}, levels = [], classes = [], initialLevelId = "", initialClassId = "" 
}) {

  const [levelId, setLevelId] = useState(String(initialLevelId || levels[0]?.id || ""));
  const [classId, setClassId] = useState(String(initialClassId || classes[0]?.id || ""));

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState([]);

  const level = levels.find((item) => String(item.id) === levelId);
  const grade = gradeNumber(level);

  const [students, setStudents] = useState([]);
  const [availableClasses, setAvailableClasses] = useState(classes);

  const [loading, setLoading] = useState(false);
  const [loadingClasses, setLoadingClasses] = useState(false);

  const [assignedIds, setAssignedIds] = useState(new Set());
  const [assignmentReady, setAssignmentReady] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const { 
    ysb_academic_year_id: academicYearId, 
    ysb_semester_id: semesterId,
    ysb_branch_id: branchId, 
    ysb_school_id: schoolId } 
  = apiContext;

  const token = localStorage.getItem("token");

  const userID = localStorage
    .getItem("id_admin")
    ?.replace(/[|&;$%@"<>()+,]/g, "");

  useEffect(() => {
    let cancelled = false;

    setStudents([]); setSelected([]); setError(""); setLoading(false);

    if (show && !academicYearId) setError("ID tahun ajaran tidak tersedia pada data semester.");

    if (!show || !levelId || !academicYearId || !semesterId || !branchId || !schoolId) return;

    setLoading(true);

    APIUS.get("/api/student-admitted/info", {
      headers: { Authorization: token },
      params: { 
        ysb_academic_year_id: academicYearId, 
        ysb_semester_id: semesterId, 
        ysb_branch_id: branchId, 
        ysb_school_id: schoolId, 
        ysb_grade_level_id: levelId 
      },
    }).then((response) => {
      if (response.data?.error) throw new Error(response.data.message);

      const groups = Array.isArray(response.data?.data) ? response.data.data : [];
      const unique = new Map();

      groups.filter((group) => 
        String(group.ysb_grade_level_id) === String(levelId)).forEach((group) => 
          (group.data_students || []).forEach((item) => {
          if (item.id) unique.set(String(item.id), { ...item, id: String(item.id), nama_siswa: item.nama_lengkap || "" });
      }));
      
      if (!cancelled) setStudents([...unique.values()].sort((a, b) => a.nama_siswa.localeCompare(b.nama_siswa, "id")));

    }).catch((err) => { 
      if (!cancelled) 
        setError(err.response?.data?.message || err.message || "Gagal mengambil daftar siswa."); 
    }).finally(() => { 
      if (!cancelled) setLoading(false); });
      return () => { cancelled = true; };
  }, [show, levelId, academicYearId, semesterId, branchId, schoolId, token]);

  useEffect(() => {
    let cancelled = false;

    setAvailableClasses([]); setAssignedIds(new Set()); setAssignmentReady(false);

    if (!show || !levelId) { setLoadingClasses(false); return; }

    setLoadingClasses(true);

    APIMS.get("/api/kelas", { 
      headers: { Authorization: token }, 
      params: { 
        page: 1, 
        limit: 1000, 
        ascending: 1, 
        branch: branchId, 
        level: localStorage.getItem("level") 
      } 
    })
    .then(async (response) => {
      if (response.data?.error) throw new Error(response.data.message);
      
      const rows = (response.data?.data || []).filter((item) => String(item.ysb_grade_level_id) === String(levelId) && String(item.ysb_school_id) === String(schoolId) && String(item.ysb_branch_id) === String(branchId));

      const results = await Promise.all(rows.map((item) => APISD.get("/api/student-kelas", {
        headers: { Authorization: token },
        params: { 
          ysb_academic_year_id: academicYearId, 
          ysb_semester_id: semesterId, 
          ysb_branch_id: branchId, 
          ysb_school_id: schoolId, 
          ysb_grade_level_id: levelId, 
          ysb_kelas_id: item.id 
        },
      })));

      const assigned = new Set();

      results.forEach((result) => {
        if (result.data?.error) throw new Error(result.data.message);

        const groups = Array.isArray(result.data?.data) ? result.data.data : [];

        groups.forEach((group) => (group.students || []).forEach((student) => {
          const id = student.ysb_student_id || student.id;
          if (id) assigned.add(String(id));
         }));
      });

      if (!cancelled) {
        setAvailableClasses(rows); setAssignedIds(assigned); setAssignmentReady(true);

        setSelected((before) => before.filter((id) => !assigned.has(String(id))));

        setClassId((value) => rows.some((item) => String(item.id) === value) ? value : "");
      }
    }).catch((err) => { 
      if (!cancelled) setError(err.response?.data?.message || "Gagal mengambil kelas tujuan."); 
    }).finally(() => { if (!cancelled) setLoadingClasses(false); });
    return () => { cancelled = true; };
  }, [show, levelId, academicYearId, semesterId, schoolId, branchId, token]);

  const save = async () => {
    if (saving || loading || loadingClasses || !assignmentReady || !selected.length || !classId) return;

    const studentIds = selected.filter((id) => !assignedIds.has(String(id)));
    if (!studentIds.length) return;

    setSaving(true); setError("");

    try {
      const response = await APISD.post("/api/student-kelas/store", {
        ysb_academic_year_id: academicYearId, 
        ysb_branch_id: branchId, 
        ysb_school_id: schoolId,
        ysb_kelas_id: classId, 
        ysb_semester_id: [semesterId], 
        ysb_student_id: studentIds,
        user_id : userID
      }, 
      { headers: { 
          Authorization: token, 
          "Content-Type": "application/json" 
        } 
      });

      if (response.data?.error) throw new Error(response.data.message);

      setAssignedIds((before) => new Set([...before, ...studentIds]));
      setSelected([]);
      await onSave?.();
    } catch (err) { 
        setError(err.response?.data?.message || err.message || "Gagal menyimpan siswa."); 
    } finally { setSaving(false);}
  };

  const filtered = useMemo(() => 
    students.filter((item) => assignmentReady && !assignedIds.has(String(item.id)) && `${item.nama_siswa} ${item.nisn}`.toLowerCase().includes(query.toLowerCase().trim())), [students, query, assignedIds, assignmentReady]);

  const selectedSet = new Set(selected);
  
  const allVisible = filtered.length > 0 && filtered.every((item) => selectedSet.has(item.id));

  const className = availableClasses.find((item) => String(item.id) === classId)?.name_kelas || "kelas tujuan";

  const title = [1, 7, 10].includes(grade) ? "Daftar Siswa Baru" : "Daftar Siswa Pindahan";

  if (!show) return null;

  const close = () => { if (!saving) onHide?.(); };
  
  const selectLevel = (value) => { setLevelId(value); setClassId(""); setSelected([]); setQuery(""); };

  const toggle = (id) => setSelected((before) => before.includes(id) ? before.filter((value) => value !== id) : [...before, id]);

  const toggleAll = () => setSelected((before) => allVisible ? before.filter((id) => !filtered.some((item) => item.id === id)) : [...new Set([...before, ...filtered.map((item) => item.id)])]);

  return (
    <div 
      className="tsm-backdrop" 
      onMouseDown={(event) => { 
        if (event.target === event.currentTarget) close(); 
      }}>
      <section 
        className="tsm-dialog" 
        role="dialog" 
        aria-modal="true" 
        aria-labelledby="tsm-title"
      >

        <header className="tsm-header">
          <div>
            <h3 id="tsm-title">Konfigurasi Siswa Baru</h3>
            <p>Pilih siswa baru yang akan dimasukkan ke rombel tujuan.</p>
          </div>

          <button 
            type="button" 
            className="tsm-close" 
            onClick={close} 
            aria-label="Tutup"
          >
            ×
          </button>
        </header>

        <div className="tsm-body">
          <div className="tsm-context">
            <div>
              <small>Tahun Ajaran</small>
              <strong>{context.namaTA || "-"}</strong>
            </div>

            <div>
              <small>Semester</small>
              <strong>Semester {context.semester || "-"}</strong>
            </div>

            <div>
              <small>Sekolah</small>
              <strong>{context.school || "-"}</strong>
            </div>

            <label>
              <small>Tingkatan Tujuan</small>
              <select 
                disabled={saving} 
                value={levelId} 
                onChange={(event) => selectLevel(event.target.value)}
              >
                <option value=""> Pilih tingkatan </option>
                {levels.map((item) => 
                  <option 
                    key={item.id} 
                    value={item.id}
                  > 
                    {item.name_level}
                  </option>
                )}
              </select>
            </label>

            <label>
              <small>Nama Kelas Tujuan</small>
              <select 
                disabled={saving || loadingClasses} 
                value={classId} 
                onChange={(event) => setClassId(event.target.value)}
              >
                <option value="">Pilih kelas</option>
                
                {availableClasses.map((item) => 
                  <option 
                    key={item.id} 
                    value={item.id}
                  >
                    {item.name_kelas}</option>
                  )}
                </select>
            </label>
          </div>

          <div className="tsm-toolbar">
            <div>
              <h4>{title}</h4>
              <p>Hanya menampilkan siswa yang belum memiliki kelas.</p>
            </div>

            <input 
              type="search" 
              placeholder="Cari nama atau NISN..." 
              value={query} 
              onChange={(event) => setQuery(event.target.value)} 
            />
          </div>

          {error && 
            <p role="alert" style={{ color: "#c0392b", marginBottom: 10 }}>
              {error}
            </p>
          }

          <div className="tsm-list">
            <label className="tsm-select-all">
              <input 
                type="checkbox" 
                checked={allVisible} 
                onChange={toggleAll} 
                disabled={saving || !filtered.length} 
              />
              <span>Pilih semua siswa</span>
              <b>{selected.length} dipilih</b>
            </label>

            <div className="tsm-rows">
              {loading || loadingClasses ? 
                <p className="tsm-empty">
                  Memuat daftar siswa...
                </p> : filtered.length ? 
                filtered.map((item) => 
                  <label 
                    className="tsm-row" 
                    key={item.id}
                  >
                    <input 
                      type="checkbox" 
                      disabled={saving} 
                      checked={selectedSet.has(item.id)} 
                      onChange={() => toggle(item.id)} 
                    />
                    <span className="tsm-avatar">
                      {item.nama_siswa.split(" ").slice(0, 2).map((part) => part[0]).join("")}
                    </span>
                    <span>
                      <strong>{item.nama_siswa}</strong>
                      <small>NISN: {item.nisn}</small>
                    </span>
                  </label>) : 
                  <p className="tsm-empty">Tidak ada siswa yang sesuai.</p>
                }
            </div>
          </div>
        </div>

        <footer className="tsm-footer">
          <span>
            <strong>{selected.length}</strong> 
            siswa akan dimasukkan ke 
            <strong>{className}</strong>
          </span>
          <button 
            type="button" 
            className="tsm-cancel" 
            onClick={close}
          >
            Batal
          </button>
          
          <button 
            type="button" 
            className="tsm-submit" 
            disabled={saving || loading || loadingClasses || !assignmentReady || !academicYearId || !selected.length || !levelId || !classId} 
            onClick={save}
          >
            {saving ? "Menyimpan..." : "Masukkan ke Rombel"}
          </button>
        </footer>

      </section>
    </div>
  );
}
